import { type FC, useState } from 'react';
import { difference, isEqual } from 'lodash-es';

import {
	type Timeline,
	type TimelineJsonData,
	type TimelineSlide
} from '@editor2/types/pageElements';
import ChangesDiff from '../common/ChangesDiff';
import ExpandChangesButton from '../common/ExpandChangesButton';
import ChildrenChangesWrapper from '../common/ChildrenChangesWrapper';
import DeletedItem from '../common/DeletedItem';
import { elementHeader } from '../styles';

interface Props {
	initialElement: Timeline;
	currentElement: Timeline;
	data: TimelineJsonData;
}

const TimelineDataDiff: FC<Props> = (props) => {
	const { initialElement, currentElement, data } = props;

	const [showChildren, setShowChildren] = useState(true);

	const getSlideChanges = (initialSlide, currentSlide) => {
		if (!initialSlide) return currentSlide;

		const changes: Partial<TimelineSlide> = Object.keys(currentSlide).reduce((acc, curr) => {
			if (!isEqual(initialSlide[curr], currentSlide[curr])) acc[curr] = currentSlide[curr];
			return acc;
		}, {});
		return changes;
	};

	const checkTitleSlideChanges = () => {
		const slideChanges = getSlideChanges(initialElement?.data?.title, data?.title);
		return !!Object.keys(slideChanges).length;
	};

	return (
		<>
			{data.events && !showChildren && (
				<ExpandChangesButton onClick={() => setShowChildren(true)} />
			)}

			{showChildren && (
				<ChildrenChangesWrapper setShowChildren={setShowChildren}>
					{data.title && checkTitleSlideChanges() && (
						<ChangesDiff
							key={0}
							index={0}
							initialIndex={0}
							initial={initialElement?.data?.title as any}
							attributeChanges={getSlideChanges(initialElement?.data?.title, data?.title)}
							title={`Slide 0. ${data?.title?.text?.headline || ''}`}
							styles={elementHeader}
							isNewElement={!initialElement?.data?.title}
						/>
					)}

					{data.events &&
						data.events.map((slide, i) => {
							const { unique_id } = slide;

							const initialIndex = initialElement?.data?.events.findIndex(
								(el) => el.unique_id === unique_id
							);
							const initialSlide = initialElement?.data?.events[initialIndex];
							const slideChanges = getSlideChanges(initialSlide, slide);

							return (
								(!!Object.keys(slideChanges).length || initialIndex !== i) && (
									<ChangesDiff
										key={i}
										index={i}
										initialIndex={initialIndex}
										initial={initialSlide as any}
										attributeChanges={slideChanges}
										title={`Slide ${i + 1}. ${slide?.text?.headline || ''}`}
										styles={elementHeader}
										isNewElement={!initialElement || initialIndex === -1}
									/>
								)
							);
						})}

					{data.events &&
						difference(
							initialElement?.data?.events.map((el) => el.unique_id) || [],
							currentElement.data?.events.map((el) => el.unique_id)
						).map((unique_id) => {
							return (
								<DeletedItem
									key={unique_id}
									title={`Slide: ${
										initialElement?.data?.events.find((el) => el.unique_id === unique_id)?.text
											.headline
									}`}
									styles={elementHeader}
								/>
							);
						})}
				</ChildrenChangesWrapper>
			)}
		</>
	);
};

export default TimelineDataDiff;
